/**
 * BridgeFetchBadge — the list row's live bridge state (BLU-880): the same
 * step vocabulary as the dialog's stepper, in a pill. Replaces the static
 * "Auto-fetching from source" pill whenever the row carries a live
 * `bridgeFetchState` (the DAO's typed projection), so the list and the dialog
 * can never disagree about where the bridge is. Failed rows are not this
 * badge's job — the poisoned pill owns them.
 *
 * Quiet-grey `muted` variant, ring not border (DESIGN.md §13.1) — it is a
 * watch-list state, not a call to action. `role="status"` announces changes.
 */
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { BridgeFetchState } from '../bridgeFetchState';

interface BridgeFetchBadgeProps {
  state: Exclude<BridgeFetchState, 'failed'>;
}

export function BridgeFetchBadge({ state }: BridgeFetchBadgeProps) {
  const { t } = useTranslation('production');
  return (
    <Badge
      variant="muted"
      role="status"
      className="gap-1 ring-1 ring-border"
      data-testid="bridge-fetch-badge"
      data-state={state}
    >
      <RefreshCw className="h-3 w-3 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {t('issues.printFiles.bridgeFetch.badge', { step: t(`issues.printFiles.bridgeFetch.step.${state}`) })}
    </Badge>
  );
}
